import {
  SPEECH_ENGINE_HINTS,
  SPEECH_ENGINE_LABELS,
  SPEECH_ENGINES,
  type SpeechEngine,
  type SpeechSettingsView,
} from '@fos/shared/contracts/speech';
import { Mic } from 'lucide-react';
import { type FormEvent, useEffect, useState } from 'react';
import { errorMessage, validationIssues } from '../../../lib/api';
import { trpc } from '../../../lib/trpc';
import { ContextBanner, FormMessage } from '../../../ui/Blocks';
import { Button } from '../../../ui/Button';
import { Field } from '../../../ui/Field';
import { Input } from '../../../ui/Input';
import { SectionHeader } from '../../../ui/PageHeader';
import { Section } from '../../../ui/Section';
import { Segmented } from '../../../ui/Segmented';
import { ErrorState, Skeleton } from '../../../ui/States';
import { Status } from '../../../ui/Status';
import { toast } from '../../../ui/Toast';

/**
 * Ustawienia → AI i pamięć → Model i limity → „Mowa na tekst” (Fala 10, tor bubble-voice; bez projektu w Figmie —
 * wzorzec sekcji ustawień): silnik mowy dla mikrofonu w czacie Copilota i w poleceniu głosem (przeglądarka / OpenAI /
 * automatycznie), model transkrypcji OpenAI i cena za minutę (do budżetu AI). Widzą i zmieniają: właściciel,
 * administrator, manager. Stan transkrypcji na serwerze (klucz OpenAI z Integracji, model, cena) obok formularza.
 */
export function SpeechSettingsSection() {
  const query = trpc.voice.speechSettings.useQuery({});
  return (
    <Section aria-labelledby="speech-settings" className="flex flex-col gap-4">
      <SectionHeader tone="strong" title={<span id="speech-settings">Mowa na tekst</span>} />
      {query.isPending ? (
        <Skeleton className="h-40 rounded-xl" />
      ) : query.isError ? (
        <ErrorState compact onRetry={() => void query.refetch()} retrying={query.isFetching} />
      ) : (
        <SpeechForm view={query.data} />
      )}
    </Section>
  );
}

function SpeechForm({ view }: { view: SpeechSettingsView }) {
  const utils = trpc.useUtils();
  const save = trpc.voice.updateSpeechSettings.useMutation();
  const [engine, setEngine] = useState<SpeechEngine>(view.settings.engine);
  const [model, setModel] = useState(view.settings.model ?? '');
  const [price, setPrice] = useState(view.settings.pricePerMinutePln ?? '');
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    setEngine(view.settings.engine);
    setModel(view.settings.model ?? '');
    setPrice(view.settings.pricePerMinutePln ?? '');
  }, [view]);

  const dirty =
    engine !== view.settings.engine ||
    model.trim() !== (view.settings.model ?? '') ||
    (view.showMoney && price.trim().replace(',', '.') !== (view.settings.pricePerMinutePln ?? ''));

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setFieldErrors({});
    try {
      await save.mutateAsync({
        engine,
        model: model.trim() || null,
        ...(view.showMoney ? { pricePerMinutePln: price.trim() ? price.trim().replace(',', '.') : null } : {}),
      });
      await Promise.all([
        utils.voice.speechSettings.invalidate(),
        utils.voice.speechStatus.invalidate(),
        utils.ai.getSettings.invalidate(),
      ]);
      toast.success('Zapisano ustawienia mowy');
    } catch (err) {
      const issues = validationIssues(err);
      if (issues.length) setFieldErrors(Object.fromEntries(issues.map((i) => [i.path, i.message])));
      setError(errorMessage(err, 'Nie udało się zapisać ustawień mowy.'));
    }
  };

  const server = view.status.server;
  const readOnly = !view.canEdit;
  return (
    <form onSubmit={(e) => void submit(e)} className="flex flex-col gap-4" aria-label="Mowa na tekst" noValidate>
      <p className="text-body-s text-fg-secondary">
        Mikrofon w czacie Copilota i w poleceniu głosem. Tekst trafia do pola wiadomości do poprawy przed wysłaniem.
        Nagranie nie jest zapisywane. Rozpoznawanie w przeglądarce (Chrome) może korzystać z usług Google — do Fravity
        OS trafia tylko tekst.
      </p>
      <Field label="Silnik mowy" hint={SPEECH_ENGINE_HINTS[engine]}>
        <Segmented
          aria-label="Silnik mowy"
          value={engine}
          onValueChange={(v) => !readOnly && setEngine(v)}
          options={SPEECH_ENGINES.map((e) => ({ value: e, label: SPEECH_ENGINE_LABELS[e] }))}
        />
      </Field>
      <div className="grid gap-4 md:grid-cols-2">
        <Field
          label="Model transkrypcji (OpenAI)"
          hint="Na przykład gpt-transcribe. Klucz OpenAI: Ustawienia → Integracje → Dostawca AI."
          error={fieldErrors.model ?? null}
        >
          <Input
            value={model}
            onChange={(e) => setModel(e.target.value)}
            placeholder="gpt-transcribe"
            disabled={readOnly || engine === 'browser'}
            autoComplete="off"
            spellCheck={false}
            mono
          />
        </Field>
        {view.showMoney && (
          <Field
            label="Cena za minutę nagrania (zł)"
            hint="Liczona do budżetu AI. Przy ustawionym budżecie bez ceny transkrypcja nie działa."
            error={fieldErrors.pricePerMinutePln ?? null}
          >
            <Input
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              placeholder="0,0175"
              inputMode="decimal"
              disabled={readOnly || engine === 'browser'}
              mono
            />
          </Field>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <Mic size={16} strokeWidth={1.75} aria-hidden className="text-fg-muted" />
        <span className="text-body-s text-fg-secondary">Transkrypcja przez OpenAI:</span>
        {server.available ? (
          <Status tone={server.simulated ? 'accent' : 'success'}>{server.simulated ? 'Symulacja' : 'Działa'}</Status>
        ) : (
          <Status tone={server.reason === 'browser_only' ? 'neutral' : 'warning'}>
            {server.reason === 'browser_only' ? 'Wyłączona' : 'Niedostępna'}
          </Status>
        )}
      </div>
      {!server.available && server.message && server.reason !== 'browser_only' && (
        <ContextBanner tone="info">{server.message}</ContextBanner>
      )}
      {error && <FormMessage>{error}</FormMessage>}
      {readOnly ? (
        <p className="text-caption text-fg-muted">
          {view.version === null
            ? 'Najpierw zapisz ustawienia „Model i limity”, potem ustawienia mowy.'
            : 'Ustawienia mowy zmienia właściciel, administrator albo manager.'}
        </p>
      ) : (
        <div>
          <Button type="submit" variant="secondary" loading={save.isPending} disabled={!dirty}>
            Zapisz ustawienia mowy
          </Button>
        </div>
      )}
    </form>
  );
}
